import {
  APP_TIMEZONE,
  DURATION_PRESETS,
  MAX_DURATION_MINUTES,
  MIN_ARRIVAL_WINDOW_MINUTES,
  MIN_DURATION_MINUTES,
  REMINDER_OPTIONS,
  formatDuration,
  groszeToInput,
  localToUtc,
  normalizePhone,
  parseMoney,
  type CurrentUser,
  type Visibility,
  type VisitCreateRequest,
  type VisitDto,
} from '@kominiarz/shared';
import { Save } from 'lucide-react';
import { DateTime } from 'luxon';
import { useCallback, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useCurrentUser } from '../../auth/useAuth';
import { Alert } from '../../components/Alert';
import { OfflineNotice } from '../../components/OfflineNotice';
import { useOnline } from '../../lib/appStatus';
import { Button } from '../../components/Button';
import { ColorDot } from '../../components/ColorDot';
import { CheckboxList } from '../../components/form/CheckboxList';
import { ChoiceGroup } from '../../components/form/ChoiceGroup';
import { SelectInput } from '../../components/form/SelectInput';
import { Switch } from '../../components/form/Switch';
import { TextArea } from '../../components/form/TextArea';
import { TextInput } from '../../components/form/TextInput';
import { Spinner } from '../../components/Loading';
import { PageBody } from '../../components/PageBody';
import { PageHeader } from '../../components/PageHeader';
import { Section } from '../../components/Section';
import { useToast } from '../../components/Toast';
import { visitClientName } from '../../components/VisitStatus';
import { VisitPlaceFields, type PlaceState } from './VisitPlaceFields';
import { ApiError, errorMessage } from '../../lib/api';
import { local, timeRange, todayLocal } from '../../lib/calendarDates';
import { useUserOptions } from '../../lib/usersApi';
import { useCreateVisit, useOverlaps, useServiceTypes, useUpdateVisit, useVisit } from '../../lib/visitsApi';
import styles from './visits.module.css';

interface FormState {
  date: string;
  time: string;
  /** End of the arrival window ("between time and windowEnd"); null = an exact time. */
  windowEnd: string | null;
  duration: number;
  customDuration: string | null;
  address: string;
  phone: string;
  /** In the order ticked; the first is the main service. */
  serviceTypeIds: number[];
  description: string;
  amount: string;
  /** In the order ticked; the first person leads. */
  assigneeIds: number[];
  visibility: Visibility;
  sharedWith: number[];
  /** Carried over unchanged until the client picker arrives (stage 3). */
  clientId: number | null;
  clientName: string | null;
  objectId: number | null;
  /** Per-visit reminder: null = the assignee's setting, 0 = none. */
  reminderMinutes: number | null;
  lat: number | null;
  lng: number | null;
}

const REMINDER_FORM_OPTIONS = [
  { value: 'default', label: 'Jak w ustawieniach wykonawcy' },
  ...REMINDER_OPTIONS.map((m) => ({ value: String(m), label: `${formatDuration(m)} przed wizytą` })),
  { value: '0', label: 'Bez przypomnienia' },
];

type Errors = Partial<Record<keyof FormState | 'startAt', string>>;

const isDate = (v: string | null): v is string => !!v && /^\d{4}-\d{2}-\d{2}$/.test(v);
const isTime = (v: string | null): v is string => !!v && /^\d{2}:\d{2}$/.test(v);
const minutesOf = (time: string) => Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5));

function fromVisit(v: VisitDto, user: CurrentUser): FormState {
  const start = local(v.startAt);
  return {
    date: start.toISODate()!,
    time: start.toFormat('HH:mm'),
    windowEnd: v.arrivalWindowMinutes ? start.plus({ minutes: v.arrivalWindowMinutes }).toFormat('HH:mm') : null,
    duration: v.durationMinutes,
    customDuration: (DURATION_PRESETS as readonly number[]).includes(v.durationMinutes) ? null : String(v.durationMinutes),
    address: v.address ?? '',
    phone: v.phoneOverride ?? '',
    serviceTypeIds: v.serviceTypes.map((t) => t.id),
    description: v.description ?? '',
    amount: groszeToInput(v.amountGrosze),
    assigneeIds: v.assignees.length ? v.assignees.map((u) => u.id) : [user.id],
    visibility: v.visibility,
    sharedWith: v.sharedWith,
    clientId: v.client?.id ?? null,
    clientName: visitClientName(v),
    objectId: v.object?.id ?? null,
    reminderMinutes: v.reminderMinutes,
    lat: v.lat,
    lng: v.lng,
  };
}

function emptyState(user: CurrentUser, date: string, time: string): FormState {
  const duration = user.settings.lastDurationMinutes;
  return {
    date,
    time,
    windowEnd: null,
    duration,
    customDuration: (DURATION_PRESETS as readonly number[]).includes(duration) ? null : String(duration),
    address: '',
    phone: '',
    serviceTypeIds: [],
    description: '',
    amount: '',
    assigneeIds: [user.id],
    visibility: 'private',
    sharedWith: [],
    clientId: null,
    clientName: null,
    objectId: null,
    reminderMinutes: null,
    lat: null,
    lng: null,
  };
}

/** Field names in API errors → form fields. */
const API_FIELD: Record<string, keyof Errors> = {
  startAt: 'time',
  durationMinutes: 'duration',
  arrivalWindowMinutes: 'windowEnd',
  amountGrosze: 'amount',
};

// ---------------------------------------------------------------------------

export function NewVisitPage() {
  const user = useCurrentUser();
  const [params] = useSearchParams();
  const sourceId = Number(params.get('z')) || null;
  const nextYear = params.get('zaRok') === '1';
  const source = useVisit(sourceId);

  if (sourceId && source.isPending) {
    return (
      <>
        <PageHeader title="Nowa wizyta" back="/kalendarz" />
        <PageBody>
          <Spinner />
        </PageBody>
      </>
    );
  }

  let initial: FormState;
  let title = 'Nowa wizyta';
  if (sourceId && source.data) {
    initial = fromVisit(source.data.visit, user);
    if (nextYear) {
      initial.date = local(initial.date).plus({ years: 1 }).toISODate()!;
      title = 'Wizyta za rok';
    } else {
      title = 'Kopia wizyty';
    }
  } else {
    const date = isDate(params.get('data')) ? params.get('data')! : todayLocal();
    const time = isTime(params.get('godzina')) ? params.get('godzina')! : '';
    initial = emptyState(user, date, time);
    // Opened from a client card or "Do umówienia": client, address and service chosen up front.
    const clientId = Number(params.get('klient')) || null;
    if (clientId) {
      initial.clientId = clientId;
      initial.objectId = Number(params.get('obiekt')) || null;
    }
    const service = Number(params.get('usluga'));
    if (service) initial.serviceTypeIds = [service];
  }
  return <VisitForm title={title} initial={initial} />;
}

export function EditVisitPage() {
  const user = useCurrentUser();
  const id = Number(useParams().id);
  const query = useVisit(Number.isFinite(id) ? id : null);
  if (query.isPending) {
    return (
      <>
        <PageHeader title="Edycja wizyty" back={`/wizyty/${id}`} />
        <PageBody>
          <Spinner />
        </PageBody>
      </>
    );
  }
  if (query.isError || !query.data.visit.permissions.edit) {
    return (
      <>
        <PageHeader title="Edycja wizyty" back={`/wizyty/${id}`} />
        <PageBody>
          <Alert kind="error">
            {query.isError
              ? errorMessage(query.error)
              : 'Danych tej wizyty nie możesz zmieniać – może to zrobić osoba, która ją dodała, albo kierownik.'}
          </Alert>
          <div>
            <Link to={`/wizyty/${id}`}>Wróć do wizyty</Link>
          </div>
        </PageBody>
      </>
    );
  }
  return <VisitForm title="Edycja wizyty" initial={fromVisit(query.data.visit, user)} visit={query.data.visit} />;
}

/** A first guess for "until": three hours after "from" (empty when that passes midnight). */
function defaultWindowEnd(time: string): string {
  if (!isTime(time)) return '';
  const end = minutesOf(time) + 180;
  return end < 24 * 60 ? `${String(Math.floor(end / 60)).padStart(2, '0')}:${String(end % 60).padStart(2, '0')}` : '';
}

// ---------------------------------------------------------------------------

function VisitForm({ title, initial, visit }: { title: string; initial: FormState; visit?: VisitDto }) {
  const user = useCurrentUser();
  const navigate = useNavigate();
  const toast = useToast();
  const users = useUserOptions();
  const serviceTypes = useServiceTypes();
  const create = useCreateVisit();
  const update = useUpdateVisit();
  const [s, setState] = useState<FormState>(initial);
  const [errors, setErrors] = useState<Errors>({});
  const [formError, setFormError] = useState<string | null>(null);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setState((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };
  const changePlace = useCallback((patch: Partial<PlaceState>) => {
    setState((prev) => ({ ...prev, ...patch }));
    setErrors((prev) => {
      const next = { ...prev };
      for (const key of Object.keys(patch) as Array<keyof Errors>) delete next[key];
      if ('clientId' in patch || 'objectId' in patch) delete next.address;
      return next;
    });
  }, []);

  const durationValue = s.customDuration !== null ? Number(s.customDuration) : s.duration;
  const startIso = useMemo(() => {
    if (!isDate(s.date) || !isTime(s.time)) return null;
    try {
      return localToUtc(s.date, s.time).toISOString();
    } catch {
      return null;
    }
  }, [s.date, s.time]);

  const windowMinutes = s.windowEnd !== null && isTime(s.time) && isTime(s.windowEnd) ? minutesOf(s.windowEnd) - minutesOf(s.time) : null;
  const windowValid = s.windowEnd === null || (windowMinutes !== null && windowMinutes >= MIN_ARRIVAL_WINDOW_MINUTES);

  const overlaps = useOverlaps(
    startIso && windowValid && s.assigneeIds.length > 0 && durationValue >= MIN_DURATION_MINUTES && durationValue <= MAX_DURATION_MINUTES
      ? {
          assigneeIds: s.assigneeIds,
          startAt: startIso,
          durationMinutes: durationValue,
          arrivalWindowMinutes: windowMinutes,
          excludeId: visit?.id,
        }
      : null,
  );

  // People who can be picked: active colleagues, plus since-disabled people already on the visit.
  const people = useMemo(() => {
    const list = [...(users.data ?? [])];
    for (const u of visit?.assignees ?? []) if (!list.some((x) => x.id === u.id)) list.push(u);
    return list;
  }, [users.data, visit]);
  const teamNames =
    s.assigneeIds
      .map((id) => people.find((u) => u.id === id)?.name)
      .filter(Boolean)
      .join(', ') || 'Wykonawca';

  // Sharing is only for people who would not see the visit anyway (not its author or its team).
  const shareCandidates = people.filter((u) => u.id !== user.id && !s.assigneeIds.includes(u.id));
  const sharedWith = s.sharedWith.filter((id) => shareCandidates.some((u) => u.id === id));
  const sharing = s.visibility === 'selected' && shareCandidates.length > 0;

  const typeOptions = useMemo(() => {
    const list = (serviceTypes.data ?? []).map((t) => ({ value: t.id, label: t.name }));
    for (const t of visit?.serviceTypes ?? []) if (!list.some((o) => o.value === t.id)) list.push({ value: t.id, label: t.name });
    return list;
  }, [serviceTypes.data, visit]);

  const today = todayLocal();
  const tomorrow = local(today).plus({ days: 1 }).toISODate()!;

  function validate(): { body: VisitCreateRequest } | { errors: Errors } {
    const e: Errors = {};
    if (!isDate(s.date)) e.date = 'Wybierz datę.';
    if (!isTime(s.time)) e.time = 'Wybierz godzinę.';
    if (!windowValid) e.windowEnd = `Wybierz godzinę co najmniej ${MIN_ARRIVAL_WINDOW_MINUTES} minut po godzinie „od”.`;
    if (
      !Number.isInteger(durationValue) ||
      durationValue < MIN_DURATION_MINUTES ||
      durationValue > MAX_DURATION_MINUTES
    ) {
      e.duration = `Wpisz czas trwania w minutach (od ${MIN_DURATION_MINUTES} do ${MAX_DURATION_MINUTES}).`;
    }
    if (!s.address.trim() && !s.clientId) e.address = 'Wpisz adres albo wybierz klienta.';
    if (s.phone.trim() && !normalizePhone(s.phone)) e.phone = 'Wpisz poprawny numer telefonu, np. 600 123 456.';
    const amount = s.amount.trim() ? parseMoney(s.amount) : null;
    if (s.amount.trim() && amount === null) e.amount = 'Wpisz kwotę, np. 150 albo 150,50.';
    if (s.assigneeIds.length === 0) e.assigneeIds = 'Zaznacz co najmniej jedną osobę.';
    if (sharing && sharedWith.length === 0) e.sharedWith = 'Zaznacz co najmniej jedną osobę.';
    if (Object.keys(e).length > 0 || !startIso) return { errors: e };
    return {
      body: {
        startAt: startIso,
        durationMinutes: durationValue,
        arrivalWindowMinutes: s.windowEnd !== null ? windowMinutes : null,
        address: s.address.trim() || null,
        phone: s.phone.trim() || null,
        serviceTypeIds: s.serviceTypeIds,
        description: s.description.trim() || null,
        amountGrosze: amount,
        assigneeIds: s.assigneeIds,
        visibility: sharing ? 'selected' : 'private',
        sharedWith: sharing ? sharedWith : [],
        clientId: s.clientId,
        objectId: s.objectId,
        reminderMinutes: s.reminderMinutes,
        lat: s.lat,
        lng: s.lng,
      },
    };
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    const result = validate();
    if ('errors' in result) {
      setErrors(result.errors);
      setFormError('Popraw zaznaczone pola.');
      return;
    }
    try {
      if (visit) {
        await update.mutateAsync({ id: visit.id, body: result.body });
        toast.success('Zapisano zmiany');
        navigate(`/wizyty/${visit.id}`, { replace: true });
      } else {
        const res = await create.mutateAsync(result.body);
        toast.success('Wizyta dodana');
        navigate(`/wizyty/${res.visit.id}`, { replace: true });
      }
    } catch (err) {
      if (err instanceof ApiError && Object.keys(err.fields).length > 0) {
        const mapped: Errors = {};
        for (const [k, v] of Object.entries(err.fields)) mapped[(API_FIELD[k] ?? k) as keyof Errors] = v;
        setErrors(mapped);
        setFormError('Popraw zaznaczone pola.');
      } else {
        setFormError(errorMessage(err));
      }
    }
  }

  const saving = create.isPending || update.isPending;
  const online = useOnline();
  const durationChoice = s.customDuration !== null ? 'inny' : String(s.duration);
  const startLocal = startIso ? DateTime.fromISO(startIso).setZone(APP_TIMEZONE) : null;

  return (
    <>
      <PageHeader title={title} back={visit ? `/wizyty/${visit.id}` : '/kalendarz'} />
      <PageBody>
        <form className={styles.form} onSubmit={submit} noValidate>
          <OfflineNotice />
          <Section title="Termin">
            <div className={s.windowEnd !== null ? `${styles.whenRow} ${styles.whenRowWindow}` : styles.whenRow}>
              <TextInput
                type="date"
                label="Data"
                value={s.date}
                onChange={(e) => set('date', e.target.value)}
                error={errors.date}
              />
              <TextInput
                type="time"
                label={s.windowEnd !== null ? 'Od godziny' : 'Godzina'}
                placeholder="Wybierz"
                value={s.time}
                step={300}
                onChange={(e) => set('time', e.target.value)}
                error={errors.time}
              />
              {s.windowEnd !== null && (
                <TextInput
                  type="time"
                  label="Do godziny"
                  placeholder="Wybierz"
                  value={s.windowEnd}
                  step={300}
                  onChange={(e) => set('windowEnd', e.target.value)}
                  error={errors.windowEnd}
                />
              )}
            </div>
            <div className={styles.quickDates}>
              <Button size="sm" variant={s.date === today ? 'primary' : 'secondary'} onClick={() => set('date', today)}>
                Dziś
              </Button>
              <Button size="sm" variant={s.date === tomorrow ? 'primary' : 'secondary'} onClick={() => set('date', tomorrow)}>
                Jutro
              </Button>
            </div>
            <Switch
              label="Przedział godzin"
              description="Np. między 10 a 13: przyjazd w tym czasie, potem praca."
              checked={s.windowEnd !== null}
              onChange={(on) => set('windowEnd', on ? defaultWindowEnd(s.time) : null)}
            />
            <ChoiceGroup
              legend={s.windowEnd !== null ? 'Czas pracy' : 'Czas trwania'}
              hint={s.windowEnd !== null ? 'Ile trwa sama praca na miejscu.' : undefined}
              value={durationChoice}
              columns={5}
              options={[
                ...DURATION_PRESETS.map((m) => ({ value: String(m), label: formatDuration(m) })),
                { value: 'inny', label: 'Inny' },
              ]}
              onChange={(v) => {
                if (v === 'inny') set('customDuration', String(durationValue || ''));
                else setState((prev) => ({ ...prev, duration: Number(v), customDuration: null }));
              }}
              error={s.customDuration === null ? errors.duration : undefined}
            />
            {s.customDuration !== null && (
              <TextInput
                label="Czas trwania w minutach"
                inputMode="numeric"
                value={s.customDuration}
                onChange={(e) => set('customDuration', e.target.value.replace(/\D/g, ''))}
                hint={durationValue > 0 ? `To ${formatDuration(durationValue)}.` : 'Np. 45 albo 150.'}
                error={errors.duration}
              />
            )}
            {overlaps.data && overlaps.data.length > 0 && (
              <Alert
                kind="warning"
                title={s.assigneeIds.length === 1 ? `${teamNames} ma już wizytę w tym czasie` : 'Wybrane osoby mają już wizyty w tym czasie'}
              >
                <ul className={styles.overlapList}>
                  {overlaps.data.map((o) => (
                    <li key={o.id}>
                      {s.assigneeIds.length > 1 && `${o.people.join(', ')}: `}
                      {timeRange(o.startAt, o.endAt)}{' '}
                      {o.title ? `${o.title}${o.address ? `, ${o.address}` : ''}` : '(wizyta, której nie widzisz)'}
                    </li>
                  ))}
                </ul>
                <p>Możesz mimo to zapisać tę wizytę.</p>
              </Alert>
            )}
          </Section>

          <Section title="Miejsce i kontakt">
            <VisitPlaceFields
              value={s}
              onChange={changePlace}
              errors={{ address: errors.address, phone: errors.phone, clientId: errors.clientId, objectId: errors.objectId }}
            />
          </Section>

          <Section title="Usługi">
            <CheckboxList
              chips
              legend="Typ usługi"
              hint="Opcjonalnie. Możesz zaznaczyć kilka."
              value={s.serviceTypeIds}
              options={typeOptions}
              onChange={(v) => set('serviceTypeIds', v)}
              error={errors.serviceTypeIds}
            />
            <TextArea
              label="Opis"
              optional
              value={s.description}
              onChange={(e) => set('description', e.target.value)}
              error={errors.description}
            />
            <TextInput
              label="Kwota (zł)"
              optional
              inputMode="decimal"
              placeholder="np. 200"
              value={s.amount}
              onChange={(e) => set('amount', e.target.value)}
              error={errors.amount}
            />
          </Section>

          <Section title="Kto jedzie i kto widzi">
            <CheckboxList
              chips
              legend="Wykonawcy"
              hint="Możesz zaznaczyć kilka osób – wizyta pojawi się w kalendarzu każdej z nich."
              value={s.assigneeIds}
              options={people.map((u) => ({
                value: u.id,
                label: (
                  <span className={styles.person}>
                    <ColorDot color={u.color} size="sm" /> {u.id === user.id ? `${u.name} (ja)` : u.name}
                  </span>
                ),
              }))}
              onChange={(v) => set('assigneeIds', v)}
              error={errors.assigneeIds}
            />
            <SelectInput
              label="Przypomnienie"
              hint="Powiadomienie na telefon przed wizytą, dla każdego wykonawcy."
              value={s.reminderMinutes === null ? 'default' : String(s.reminderMinutes)}
              options={REMINDER_FORM_OPTIONS}
              onChange={(e) => set('reminderMinutes', e.target.value === 'default' ? null : Number(e.target.value))}
            />
            {/* When everyone is already on the visit there is nobody left to share it with. */}
            {shareCandidates.length > 0 && (
              <ChoiceGroup<Visibility>
                legend="Kto jeszcze widzi wizytę"
                hint="Autor, wykonawcy i kierownik widzą wizytę zawsze."
                value={s.visibility}
                columns={2}
                options={[
                  { value: 'private', label: 'Nikt więcej' },
                  { value: 'selected', label: 'Wybrane osoby' },
                ]}
                onChange={(v) => set('visibility', v)}
              />
            )}
            {sharing && (
              <CheckboxList
                legend="Udostępnij"
                value={sharedWith}
                options={shareCandidates.map((u) => ({
                  value: u.id,
                  label: (
                    <span className={styles.person}>
                      <ColorDot color={u.color} size="sm" /> {u.name}
                    </span>
                  ),
                }))}
                onChange={(v) => set('sharedWith', v)}
                error={errors.sharedWith}
              />
            )}
          </Section>

          <div className={styles.saveBar}>
            {formError && <Alert kind="error">{formError}</Alert>}
            <div className={styles.saveButtons}>
              <Button variant="ghost" size="lg" onClick={() => navigate(visit ? `/wizyty/${visit.id}` : '/kalendarz')}>
                Anuluj
              </Button>
              <Button type="submit" variant="primary" size="lg" icon={<Save aria-hidden="true" />} disabled={saving || !online}>
                {saving ? 'Zapisywanie…' : visit ? 'Zapisz zmiany' : 'Zapisz wizytę'}
              </Button>
            </div>
            {startLocal && (
              <p className={styles.saveSummary}>
                {startLocal.setLocale('pl').toFormat('EEEE, d MMMM, HH:mm')}
                {windowMinutes ? `–${startLocal.plus({ minutes: windowMinutes }).toFormat('HH:mm')} (przyjazd)` : ''},{' '}
                {formatDuration(durationValue || 0)}, {teamNames}
              </p>
            )}
          </div>
        </form>
      </PageBody>
    </>
  );
}
